import { useState, useContext, useEffect } from "react";
import { FaCheck } from "react-icons/fa";
import { IoMdWarning } from "react-icons/io";
import UserContext from "../../User/UserContext";
import CameraContainer from "../camera/cameracontainer";

function PhotoGallery({ defaultData }) {
	const [selectedPhoto, setSelectedPhoto] = useState(null);

	return (
		<div className="bg-gray-200 rounded-md p-2 mt-2 ">
			<p className="font-bold mb-2">{`Photos Retrieved from Solar Grade (${defaultData?.photos?.length || 0})`}</p>

			<div className="flex flex-wrap gap-2 ">
				{defaultData?.photos?.length > 0 &&
					defaultData.photos.map((mapData, index) => (
						<div key={mapData.photo} className="border border-2 border-black flex items-center">
							<img
								src={mapData.photo}
								alt={mapData.description}
								className="h-16 w-16 object-cover cursor-pointer"
								onClick={() => setSelectedPhoto(mapData)}
							/>
						</div>
					))}

				{selectedPhoto && (
					<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setSelectedPhoto(null)}>
						<div className="bg-white p-4 rounded-md flex flex-col items-center justify-center">
							<p className="text-2xl font-bold">Solar Grade Photo</p>
							<p className="text-xl text-sm mb-4">{selectedPhoto.description}</p>
							<img
								src={selectedPhoto.photo}
								alt="Fullscreen preview"
								className=" max-w-[80vw] lg:max-w-[90vw] max-h-[85vh] w-auto object-contain"
							/>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}

export default function SolarGradeInput({
	field,
	sectionName,
	keyedSolarGradeData,
	api,
	token,
	projectName,
	templateName,
	templateTypeId,
	setLastModifiedOn,
	projectId,
	odooId,
	selectedOdooModel,
	odooInstallationName,
	odooParentName,
	initialThumbnailPathes,
	solarGradeIds,
	stage,
	isGuest,
	keyedKrummySolarGradeData,
}) {
	const key = `${sectionName} - ${field.name}`;
	const defaultData = keyedSolarGradeData[key] || {};
	let issueId = defaultData.id ?? -1;
	const krummyData = keyedKrummySolarGradeData[issueId] || null;
	const dataSource = krummyData || defaultData;

	const [initialFieldData, setInitialFieldData] = useState();
	const [hasPhotos, setHasPhotos] = useState(true);

	const env = useContext(UserContext);
	const readOnly = stage === "completed" || (isGuest && !field.public_editable);

	function fromApiFormat(apiText) {
		return apiText
			.replace(/<br\s*\/?>/g, "")
			.split("\n\n")
			.map((line) => line.trimEnd())
			.join("\n")
			.trim();
	}

	function toApiFormat(textareaText) {
		return textareaText
			.split("\n")
			.map((line) => (line.trim() === "" ? "<br />" : line))
			.join("\n\n")
			.trim();
	}

	async function updateIssue(fieldKey, value) {
		if (issueId === -1) {
			alert("Invalid issue ID");
			return;
		}

		const currentValues = {
			description: fieldKey === "description" ? value : toApiFormat(description),
			severity_level: fieldKey === "severity_level" ? value : severity,
			occurrance: fieldKey === "occurrance" ? value : occurrence,
			prevalence: fieldKey === "prevalence" ? value : prevalence,
			status: fieldKey === "status" ? value : status,
		};

		try {
			const res = await fetch(env.api + "/projectdata/solargrade", {
				method: "POST",

				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					Authorization: "Bearer " + env.token,
				},
				body: JSON.stringify({
					id: issueId,
					fields: currentValues,
				}),
			});

			if (!res.ok) {
				console.error("Failed to update issue:", await res.text());
			}
		} catch (err) {
			console.error("Error during update:", err);
		}
	}

	const [description, setDescription] = useState(fromApiFormat(dataSource.description || ""));
	const [severity, setSeverity] = useState(dataSource.severity_level || "negligible");
	const [occurrence, setOccurrence] = useState(dataSource.occurrance || 1);
	const [prevalence, setPrevalence] = useState(dataSource.prevalence || "blank");
	const [status, setStatus] = useState(dataSource.status || "open");

	return (
		<div className="bg-white rounded-md p-4 border-l-8 border-l-blue-500">
			<div className="flex gap-2 items-center">
				<p className="bg-gray-300 p-1 text-xs">Solar Grade</p>
				<p className="text-xl font-bold">{field.name}</p>
			</div>

			<p>{field.description}</p>

			<div className="flex gap-4 bg-gray-200 p-2 my-2 mb-2 rounded-md items-center">
				<p className="font-bold text-lg">Status</p>

				<div className={`flex gap-2 items-center px-2 py-1 rounded ${defaultData.condition === "pass" ? "bg-green-300" : ""}`}>
					<FaCheck /> Pass
				</div>

				<div className={`flex gap-2 items-center px-2 py-1 rounded ${defaultData.condition === "fail" ? "bg-red-300" : ""}`}>
					<IoMdWarning /> Fail
				</div>

				<div className={`px-2 py-1 rounded ${defaultData.condition === "not_applicable" ? "bg-yellow-300" : ""}`}>Not Applicable</div>
			</div>

			<div className="space-y-2">
				<div className={`relative w-full border border-gray-400 rounded-md px-2 pt-4 pb-2 ${readOnly ? "bg-gray-100" : ""}`}>
					<label className="absolute -top-2 left-2 bg-white px-1 text-sm text-gray-600 rounded-md">Description</label>
					<textarea
						rows={6}
						className={`w-full border-none focus:outline-none bg-transparent resize-none ${readOnly ? "cursor-not-allowed text-gray-600" : ""}`}
						value={description}
						onChange={readOnly ? undefined : (e) => setDescription(e.target.value)}
						onBlur={readOnly ? undefined : () => updateIssue("description", toApiFormat(description))}
						readOnly={readOnly}
					/>
				</div>

				<div className={`relative border border-gray-400 rounded ${readOnly ? "bg-gray-100" : ""}`}>
					<label className="absolute -top-2 left-2 bg-white px-1 text-sm text-gray-600 rounded-md">Severity Level</label>
					<select
						className={`w-full border-none focus:outline-none bg-transparent mt-1 px-2 pt-3 pb-2 ${
							readOnly ? "cursor-not-allowed text-gray-600" : ""
						}`}
						value={severity}
						onChange={
							readOnly
								? undefined
								: (e) => {
										const val = e.target.value;
										setSeverity(val);
										updateIssue("severity_level", val);
								  }
						}
						disabled={readOnly}>
						<option value="critical">critical</option>
						<option value="major">major</option>
						<option value="minor">minor</option>
						<option value="negligible">negligible</option>
					</select>
				</div>

				<div className="flex gap-2">
					<div className={`relative border border-gray-400 rounded px-2 pt-3 pb-2 w-full ${readOnly ? "bg-gray-100" : ""}`}>
						<label className="absolute -top-2 left-2 bg-white px-1 text-sm text-gray-600">Occurrence</label>
						<input
							type="number"
							className={`w-full border-none focus:outline-none bg-transparent mt-1 ${readOnly ? "cursor-not-allowed text-gray-600" : ""}`}
							value={occurrence}
							onChange={
								readOnly
									? undefined
									: (e) => {
											const val = parseInt(e.target.value, 10);
											setOccurrence(val);
											updateIssue("occurrance", val);
									  }
							}
							readOnly={readOnly}
						/>
					</div>
					<div className={`relative border border-gray-400 rounded w-full ${readOnly ? "bg-gray-100" : ""}`}>
						<label className="absolute -top-2 left-2 bg-white px-1 text-sm text-gray-600">Prevalence</label>
						<select
							className={`w-full border-none focus:outline-none bg-transparent mt-1 px-2 pt-3 pb-2 ${
								readOnly ? "cursor-not-allowed text-gray-600" : ""
							}`}
							value={prevalence}
							onChange={
								readOnly
									? undefined
									: (e) => {
											const val = e.target.value;
											setPrevalence(val);
											updateIssue("prevalence", val);
									  }
							}
							disabled={readOnly}>
							<option value="blank">-</option>
							<option value="isolated">Isolated</option>
							<option value="prevalent">Prevalent</option>
							<option value="endemic">Endemic</option>
							<option value="systemic">Systemic</option>
						</select>
					</div>
				</div>

				<div className={`relative border border-gray-400 rounded px-2 pt-3 pb-2 mt-2 ${readOnly ? "bg-gray-100" : ""}`}>
					<label className="absolute -top-2 left-2 bg-white px-1 text-sm text-gray-600">Status</label>
					<select
						className={`w-full border-none focus:outline-none bg-transparent mt-1 ${readOnly ? "cursor-not-allowed text-gray-600" : ""}`}
						value={status}
						onChange={
							readOnly
								? undefined
								: (e) => {
										const val = e.target.value;
										setStatus(val);
										updateIssue("status", val);
								  }
						}
						disabled={readOnly}>
						<option value="closed">Closed</option>
						<option value="open">Open</option>
						<option value="observation">Observation</option>
					</select>
				</div>
			</div>

			<PhotoGallery defaultData={defaultData} />
			<CameraContainer
				sectionName={sectionName}
				data={field}
				cameraid={field.id}
				api={api}
				token={token}
				setInitialFieldData={setInitialFieldData}
				fieldName={field.name}
				projectName={projectName}
				templateName={templateName}
				stage={stage}
				readOnly={stage === "completed" || (isGuest && !field.public_editable)}
				templateTypeId={templateTypeId}
				setHasPhotos={setHasPhotos}
				solarGradeData={{
					solar_issue_id: issueId,
					solar_visit_id: solarGradeIds?.visitId || "",
					solar_project_id: solarGradeIds?.projectId || "",
				}}
				initialThumbnailPathes={initialThumbnailPathes.filter((filterData) => {
					const duplicatePattern = / \(\d+\)$/;
					let thumbnailName = filterData.name.split(".").slice(0, -1).join(".").trim();
					if (duplicatePattern.test(thumbnailName)) {
						thumbnailName = thumbnailName.replace(duplicatePattern, "");
					}
					let testFieldThmbName =
						sectionName.replaceAll("/", "-") +
						" - " +
						field.name
							.replaceAll("/", "_")
							.replaceAll("\\", "_")
							.replaceAll("<", "_")
							.replaceAll(">", "_")
							.replaceAll(":", "_")
							.replaceAll('"', "_")
							.replaceAll("|", "_")
							.replaceAll("?", "_")
							.replaceAll("*", "_")
							.replaceAll(".", "_")
							.trim();
					return testFieldThmbName.trim() === thumbnailName.trim();
				})}
				setLastModifiedOn={setLastModifiedOn}
				projectId={projectId}
				odooId={odooId}
				selectedOdooModel={selectedOdooModel}
				odooInstallationName={odooInstallationName}
				odooParentName={odooParentName}
			/>
		</div>
	);
}
